// 크래시 블랙박스 — 「죽은 세션은 말을 못 한다. 다음 세션이 대신 말한다」
//
// ── 왜 신설했나 ────────────────────────────────────────────────────────────────
// 실측: 폰이 14:28:06 에 게임에 들어와 **3분 만에 끊겼는데**
// `client_debug` 에는 **한 줄도 안 왔다**. nginx 는 그 3분을 다 기록하고 있다
// (roads/chunks·partitions/chunks 를 계속 받다가 14:31:06 에 뚝 끊김).
//
// ★ 즉 우리가 여태 본 `RangeError` 들은 **살아남은 크래시**였다. PC 는 heap 이 커서
//   할당 하나가 실패해도 JS 가 계속 돌아 리포터가 로그를 보낼 수 있었다. 폰은 그 전에
//   **렌더러 프로세스가 통째로 죽는다**(안드로이드 크롬의 OOM kill) — 그러면 `pagehide`
//   핸들러도, `sendBeacon` 도 돌 기회가 없다. **로그가 안 남는 게 정상이고**, 그래서
//   「모바일이 터진다」는 신고가 여태 한 번도 증거를 못 만들었다.
//
// ★ 답은 「죽기 전에 보내기」가 아니라 **「죽기 전에 적어 두기」**다. localStorage 는
//   동기라 프로세스가 죽어도 이미 디스크에 있다. 다음 부팅이 그걸 읽어 유언을 대신 전한다.
//
// ── 서버 자동 전송 제거 ─────────────────────────────────────────
// 예전엔 `reportLastSession()`이 지난 세션의 유언을 `clientLogReporter` 로 서버에 자동
// 전송했다. 로컬 개발 + chrome-devtools-mcp 로 직접 렌더러 상태를 볼 수 있게 되면서
// 그 자동 전송 채널은 없앴다(명관님 지시) — 여기 있는 건 이제 **전부 로컬 저장/열람**뿐이다.
// 4초 heartbeat(`blackboxTick`)와 이력(`pushHistory`/`getBlackboxHistory`)은 서버로
// 아무것도 보내지 않는다 — 로그인 화면 「크래시 로그」 버튼(`CrashLogPanel.jsx`)이 유저
// 본인 기기에 쌓인 기록을 직접 보여줄 뿐이다.

import { heap, top as memTop, ledger } from './memBudget.js';

const KEY = 'sg_blackbox_v1';
const MIN_ALIVE_MS = 20000;   // 이보다 짧게 살다 간 건 「죽었다」가 아니라 「안 들어왔다」에 가깝다

// ── 유저가 직접 보는 최근 크래시 이력 ──────────────────────────────
// 진단에 필요한 값(생존시간·heap)은 이미 여기서 계산이 끝나 있다 — 서버 왕복 없이 그대로
// localStorage 에 몇 줄 더 쌓아 두면 유저가 로그인 화면 버튼 하나로 직접 본다.
// `reportLastSession()`이 표식을 지우기 **전에** 압축된 사본을 이 이력에 남긴다.
const HISTORY_KEY = 'sg_blackbox_history_v1';
const HISTORY_MAX = 10;

function pushHistory(entry) {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    arr.unshift(entry);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(arr.slice(0, HISTORY_MAX)));
  } catch (_) { /* 저장공간이 꽉 찼으면 조용히 포기 */ }
}

/** 로그인 화면 「크래시 로그」 버튼이 읽는 자리 — 최근 항목이 앞. */
export function getBlackboxHistory() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

let bootAt = 0;
let ticks = 0;
// 치명 기록이 찍혔나 — 찍혔으면 정기 tick 이 덮지 못한다(아래 blackboxTick 주석).
let sealed = false;
// census(memCensus.js)가 계산해 둔 상주표 요약을 있으면 실어 나른다(재계산 없음).
// 지금은 memCensus 가 자동 리포팅을 안 하므로 대부분 비어 있지만, setCensusSnapshot 을
// 다시 부르는 경로가 생기면 여기 그대로 얹힌다 — 로컬 이력에만 쓰이므로 비용이 없다.
let censusSnap = null;

/** 계산해 둔 상주표 요약(한 줄)을 다음 tick 부터 실어 나른다. */
export function setCensusSnapshot(line) {
  censusSnap = line;
}

/**
 * 4초마다(감시와 같은 주기) 지금 상태를 덮어쓴다.
 * localStorage 쓰기는 동기라 프로세스가 죽어도 마지막 값이 남는다 — 그게 이 파일의 전부다.
 * 문자열 300B 남짓이라 비용은 무시할 수준이지만, **씬을 훑지는 않는다**(census 는 비싸다).
 */
export function blackboxTick(extra = {}) {
  if (typeof localStorage === 'undefined') return;
  if (!bootAt) bootAt = Date.now();
  // ★ 치명 기록은 봉인한다.
  //   `webglcontextlost` 는 **탭을 안 죽인다** — JS 도 R3F 루프도 계속 돈다. 그래서 4초 뒤
  //   정기 tick 이 같은 키를 덮어써서 **유언이 통째로 사라졌다**(강제 loseContext 재현:
  //   남은 값이 `fatal` 없는 평범한 tick 이었다). 다음 부팅은 그걸 읽고 「정상」으로 읽는다.
  //   한 번 fatal 이 찍히면 그 뒤 정기 tick 은 무시하고, 다른 fatal 만 덮어쓸 수 있게 둔다.
  if (sealed && !extra.fatal) return;
  if (extra.fatal) sealed = true;
  ticks++;
  const h = heap();
  const L = ledger();
  try {
    localStorage.setItem(KEY, JSON.stringify({
      t: Date.now(),
      alive: Math.round((Date.now() - bootAt) / 1000),
      // heap 은 iOS·Safari 가 안 준다 → null. 그래도 아래 원장은 **우리가 세는 값**이라
      // 모든 기기에서 나온다. 「폰에서만 죽는데 폰에서만 안 보이는」 상황을 피하는 축이다.
      heapMB: h ? Math.round(h.used / 1048576) : null,
      limMB: h ? Math.round(h.limit / 1048576) : null,
      ledMB: +(L.curTotal / 1048576).toFixed(1),
      pkMB: +(L.peakTotal / 1048576).toFixed(1),
      top: memTop(4).map((x) => `${x.tag}=${(x.cur / 1048576).toFixed(1)}`),
      ...(censusSnap ? { census: censusSnap } : {}),
      ...extra,
    }));
  } catch (_) { /* 저장공간이 꽉 찼으면 조용히 포기 — 진단이 게임을 막으면 안 된다 */ }
}

/** 정상 종료 — 표식을 지운다. 이게 안 지워진 채 다음 부팅을 맞으면 그게 곧 「비정상」이다. */
export function blackboxClean() {
  sealed = false;
  try { localStorage.removeItem(KEY); } catch (_) {}
}

/**
 * 부팅 시 **한 번** — 지난 세션이 유언을 남겼으면 로컬 이력에 남긴다(서버 전송 없음).
 *
 * ⚠ 오탐을 줄이는 두 가지: ① 20초 미만 생존은 무시(그냥 껐다 켠 것) ② 표식은 읽는 즉시 지운다.
 */
export function reportLastSession() {
  if (typeof localStorage === 'undefined') return null;
  let raw = null;
  try { raw = localStorage.getItem(KEY); } catch (_) { return null; }
  if (!raw) return null;
  try { localStorage.removeItem(KEY); } catch (_) {}
  let s = null;
  try { s = JSON.parse(raw); } catch (_) { return null; }
  if (!s || (s.alive || 0) < MIN_ALIVE_MS / 1000) return null;

  pushHistory({
    t: s.t, alive: s.alive, heapMB: s.heapMB, limMB: s.limMB,
    ledMB: s.ledMB, pkMB: s.pkMB, top: s.top, dong: s.dong, view: s.view,
  });
  return s;
}

if (typeof window !== 'undefined') {
  // 정상 종료 경로 — 탭을 닫거나 뒤로 가면 표식을 지운다. 탭 전환으로 잠깐 숨는 경우는
  // 돌아와서 tick 이 다시 쓰므로 문제가 없다(지우는 쪽이 안전한 방향이다: 오탐보다 미탐).
  window.addEventListener('pagehide', blackboxClean);
  window.__blackbox = { tick: blackboxTick, clean: blackboxClean, peek: () => {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (_) { return null; }
  } };
}
